import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Popup, ToolbarItem } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import { DataGrid, Column, Lookup, Paging, Pager, Scrolling } from "devextreme-react/data-grid";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { DateBox } from "devextreme-react/date-box";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import axios from "axios";
import { isValidGuid, loadLookupFromStaticDataSource } from "../../../../../common/Utils";
import { MESSAGES } from "../../../../../common/Messages";
import { EMPTY_GUID } from "../../../../../enum/Enum";
import {
    DiscountApplyOn,
    DiscountBasisInclusion,
    DiscountBasisMethods,
    OmTransactionStages,
    OmTransactionStatuses,
    optionsYesNo,
} from "../../../../../const/Lookup";
import notify from "devextreme/ui/notify";
import "./cumulativeDiscount.css";

const baseUrl = () => window._env_?.REACT_APP_APIS || "";

const CURRENCY_FORMAT = { type: "currency", precision: 2, currency: "USD", useGrouping: true };

const CUMULATIVE_DISCOUNT_FORM_SCHEMA = [
    {
        groupId: 1,
        label: "Discount",
        fields: [
            { name: "DiscountCode", tableName: "IDDISCOUNTHEADER", label: "Discount Code", type: "text", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "Retroactive", tableName: "IDDISCOUNTHEADER", label: "Retroactive", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "BasisMethod", tableName: "IDDISCOUNTHEADER", label: "Basis Method", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "StartingMonth", tableName: "IDDISCOUNTHEADER", label: "Start Month", type: "number", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "MonthsInPeriod", tableName: "IDDISCOUNTHEADER", label: "# of Months", type: "number", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
    {
        groupId: 2,
        label: "Apply Discounts To Orders/Cr/Adj",
        fields: [
            { name: "ApplyOnOrders", tableName: "IDDISCOUNTHEADER", label: "Open", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "ApplyOnCommitted", tableName: "IDDISCOUNTHEADER", label: "Committed", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
    {
        groupId: 3,
        label: "Apply To Discount Basis",
        fields: [
            { name: "IncludeMisc", tableName: "IDDISCOUNTHEADER", label: "Misc. Fees", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "IncludeTaxes", tableName: "IDDISCOUNTHEADER", label: "Taxes", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
];

const EMPTY_CALC_RESULT = { OutTotalDiscountBasis: 0, OutCurrentDiscountBasis: null, OutDiscountPercentage: 0 };

const formatAmount = (value) =>
    `$ ${Number(value ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatPercent = (value) => `${Number(value ?? 0).toFixed(2)}%`;

function CalculateCumulativeDiscountPopup(props) {
    const { showPopup = {}, setShowPopup, setLoadPanelVisible, oidc } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const rCustomer = showPopup.rCustomer;
    const rDiscountTable = showPopup.rDiscountTable;
    const shippingDate = showPopup.shippingDate;

    const [transactions, setTransactions] = useState([]);
    const [subDetails, setSubDetails] = useState([]);
    const [effectiveDate, setEffectiveDate] = useState(null);
    const [calcResult, setCalcResult] = useState(EMPTY_CALC_RESULT);
    const [error, setError] = useState(null);
    const [formFields, setFormFields] = useState({
        DiscountCode: "",
        Retroactive: "",
        BasisMethod: "",
        StartingMonth: "",
        MonthsInPeriod: "",
        ApplyOnOrders: "",
        ApplyOnCommitted: "",
        IncludeMisc: "",
        IncludeTaxes: "",
    });
    const formRefs = useRef({});
    const formFieldsRef = useRef(formFields);
    formFieldsRef.current = formFields;

    // Delphi FormShow refuses to open the screen at all when the order is missing any of the inputs the
    // discount calculation needs, naming the one that is missing.
    const validate = useCallback(() => {
        if (!isValidGuid(rCustomer)) return MESSAGES.CUMULATIVE_DISCOUNT_NO_CUSTOMER;
        if (!isValidGuid(rDiscountTable)) return MESSAGES.CUMULATIVE_DISCOUNT_NO_DISCOUNT_TABLE;
        if (!isValidGuid(headerRowId)) return MESSAGES.CUMULATIVE_DISCOUNT_NO_TRANSACTION;
        if (!shippingDate) return MESSAGES.CUMULATIVE_DISCOUNT_NO_SHIP_DATE;
        return null;
    }, [rCustomer, rDiscountTable, headerRowId, shippingDate]);

    const loadData = useCallback(async () => {
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            notify({ message: validationError, width: 400 }, "error", 4000);
            return;
        }

        setError(null);
        setLoadPanelVisible(true);
        const token = oidc?.user?.access_token;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
            // The basis method, the Open / Committed flags and the period on the discount table decide
            // which transactions accumulate, so the ship date and the current order go to every call.
            const discountTableRequestDto = { rowid: rDiscountTable, shipDate: shippingDate };
            const transactionsRequestDto = {
                rCustomer,
                rDiscountTable,
                currentTransRowId: headerRowId,
                shipDate: shippingDate,
            };
            const [headerRes, transRes, subDetailsRes] = await Promise.all([
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountHeader`, {
                    params: discountTableRequestDto,
                    headers,
                }),
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountTransactions`, {
                    params: transactionsRequestDto,
                    headers,
                }),
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountSubDetails`, {
                    params: discountTableRequestDto,
                    headers,
                }),
            ]);
            const header = headerRes.data || null;
            setFormFields({
                DiscountCode: header?.DiscountCode,
                Retroactive: header?.Retroactive,
                BasisMethod: header?.BasisMethod,
                StartingMonth: header?.StartingMonth,
                MonthsInPeriod: header?.MonthsInPeriod,
                ApplyOnOrders: header?.ApplyOnOrders,
                ApplyOnCommitted: header?.ApplyOnCommitted,
                IncludeMisc: header?.IncludeMisc,
                IncludeTaxes: header?.IncludeTaxes,
            });
            setTransactions(Array.isArray(transRes.data?.Transactions) ? transRes.data.Transactions : []);
            setSubDetails(Array.isArray(subDetailsRes.data?.SubDetails)
                ? subDetailsRes.data.SubDetails.map((row, i) => ({ ...row, _key: i }))
                : []);
            setEffectiveDate(subDetailsRes.data?.EffectiveDate ?? null);

            const calcRes = await axios.post(
                `${baseUrl()}/om/SalesOrderHeaderOperations/CalculateCumulativeDiscount`,
                { rCustomer, rDiscountTable, currentTransRowId: headerRowId, shipDate: shippingDate },
                { headers }
            );
            const data = calcRes.data || {};
            setCalcResult({
                OutTotalDiscountBasis: data.OutTotalDiscountBasis ?? 0,
                OutCurrentDiscountBasis: data.OutCurrentDiscountBasis ?? null,
                OutDiscountPercentage: data.OutDiscountPercentage ?? 0,
            });
        } catch (err) {
            const message = err.response?.data?.message || err.message || MESSAGES.CUMULATIVE_DISCOUNT_LOAD_FAILED;
            setError(message);
            notify({ message, width: 400 }, "error", 4000);
            setTransactions([]);
            setSubDetails([]);
            setEffectiveDate(null);
            setCalcResult(EMPTY_CALC_RESULT);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [validate, rCustomer, rDiscountTable, headerRowId, shippingDate, oidc?.user?.access_token, setLoadPanelVisible]);

    useEffect(() => {
        if (!visible) return;
        loadData();
    }, [visible, loadData]);

    const handleApplyDiscount = async () => {
        // The same guards the load runs: the popup still opens (with its error banner) when one of them
        // fails, so Apply has to re-check rather than post a payload the server cannot act on.
        const validationError = validate();
        if (validationError) {
            setError(validationError);
            notify({ message: validationError, width: 400 }, "error", 4000);
            return;
        }
        setLoadPanelVisible(true);
        setError(null);
        const token = oidc?.user?.access_token;
        // A zero or negative calculated percentage clears the discount rather than storing it - the
        // service clamps too, but onApplied has to hand the caller the value that was actually written.
        const appliedDiscountPercent = Math.max(0, calcResult.OutDiscountPercentage ?? 0);
        // Whether the percentage lands on this order alone or on every transaction in the period is the
        // discount table's Retroactive flag, so the API is given the same context the grid was built from.
        const applyRequestDto = {
            headerRowId,
            rCustomer,
            rDiscountTable,
            currentTransRowId: headerRowId,
            shipDate: shippingDate,
            outDiscountPercentage: appliedDiscountPercent,
        };
        try {
            const response = await axios.post(
                `${baseUrl()}/om/SalesOrderHeaderOperations/ApplyCumulativeDiscountToOrder`,
                applyRequestDto,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            if (response.data !== true) {
                notify({ message: MESSAGES.CUMULATIVE_DISCOUNT_NOT_APPLIED, width: 400 }, "warning", 4000);
                return;
            }
            setShowPopup((prev) => ({ ...prev, visible: false }));
            if (props.onApplied) props.onApplied(appliedDiscountPercent);
            notify({ message: MESSAGES.CUMULATIVE_DISCOUNT_APPLIED, width: 300 }, "success", 3000);
        } catch (err) {
            const message = err.response?.data?.message || err.message || MESSAGES.CUMULATIVE_DISCOUNT_APPLY_FAILED;
            setError(message);
            notify({ message, width: 400 }, "error", 4000);
        } finally {
            setLoadPanelVisible(false);
        }
    };

    const handleHiding = () => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
        setTransactions([]);
        setSubDetails([]);
        setEffectiveDate(null);
        setCalcResult(EMPTY_CALC_RESULT);
        setFormFields({});
        setError(null);
    };

    const handleFieldValueChange = useCallback(() => { }, []);
    const handleFormLoad = useCallback(() => {
        const fields = formFieldsRef.current;
        loadLookupFromStaticDataSource("BasisMethod", DiscountBasisMethods, fields, formRefs);
        loadLookupFromStaticDataSource("Retroactive", optionsYesNo, fields, formRefs);
        loadLookupFromStaticDataSource("ApplyOnOrders", DiscountApplyOn, fields, formRefs);
        loadLookupFromStaticDataSource("ApplyOnCommitted", DiscountApplyOn, fields, formRefs);
        loadLookupFromStaticDataSource("IncludeMisc", DiscountBasisInclusion, fields, formRefs);
        loadLookupFromStaticDataSource("IncludeTaxes", DiscountBasisInclusion, fields, formRefs);
    }, []);

    const title = "OM Cumulative Trade Discounts";

    return (
        <>
            {visible && (
                <Popup
                    visible={true}
                    onHiding={handleHiding}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title={title}
                    container="#root"
                    width="90vw"
                    height="90vh"
                >
                    {/* One flex column: the discount table's fields, then the tabs taking whatever is
                        left. See cumulativeDiscount.css for why the shared GridHeight* classes are
                        not used here. */}
                    {/* Delphi grpAction - the calculated basis and the two buttons. On the popup's
                        own bottom toolbar rather than inside the content, so DevExtreme reserves the
                        space for it and the grid can never render over it. */}
                    <ToolbarItem toolbar="bottom" location="before">
                        <TextBox
                            className="cumulative-discount-total"
                            label="Cumulative Discount Basis"
                            labelMode="floating"
                            value={formatAmount(calcResult.OutCurrentDiscountBasis)}
                            readOnly
                            stylingMode="outlined"
                        />
                    </ToolbarItem>
                    <ToolbarItem toolbar="bottom" location="before">
                        <TextBox
                            className="cumulative-discount-total"
                            label="Discount %"
                            labelMode="floating"
                            value={formatPercent(calcResult.OutDiscountPercentage)}
                            readOnly
                            stylingMode="outlined"
                        />
                    </ToolbarItem>
                    <ToolbarItem toolbar="bottom" location="after">
                        <Button text="Apply Discount" type="default" onClick={handleApplyDiscount} />
                    </ToolbarItem>

                    <div className="cumulative-discount-popup">
                        {error && <div className="cumulative-discount-error">{error}</div>}

                        <div className="cumulative-discount-fields">
                            <FormField
                                currrentUser={oidc?.user}
                                layoutId={EMPTY_GUID}
                                layoutName="OMCumulativeDiscountPopup"
                                defaultFormSchema={CUMULATIVE_DISCOUNT_FORM_SCHEMA}
                                formFields={formFields}
                                setformFields={setFormFields}
                                handleFieldValueChange={handleFieldValueChange}
                                DYNAMICFORMSETTING="no"
                                formRefs={formRefs}
                                onFormLoad={handleFormLoad}
                                handleSelectedItemChange={() => { }}
                                handleFocusItemIn={() => { }}
                            />
                        </div>

                        <TabPanel className="cumulative-discount-tabs">
                            <TabItem title="Transactions">
                                <div className="cumulative-discount-tab-body">
                                    <div className="cumulative-discount-grid">
                                        <DataGrid
                                            dataSource={transactions}
                                            keyExpr="RowId"
                                            height="100%"
                                            showBorders={true}
                                            columnAutoWidth={true}
                                            allowColumnReordering={true}
                                        >
                                            <Scrolling mode="standard" />
                                            <Paging defaultPageSize={10} />
                                            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 25, 50]} />
                                            <Column dataField="TransactionNumber" caption="Transaction #" />
                                            <Column dataField="TransactionDate" caption="Transaction Date" dataType="date" format="shortDate" />
                                            <Column dataField="Status" caption="Status">
                                                <Lookup dataSource={OmTransactionStatuses} displayExpr="value" valueExpr="key" />
                                            </Column>
                                            <Column dataField="ItemTotal" caption="Item Total" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="DiscountableTotal" caption="Discount Basis" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="FeesAmount" caption="Fees" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="TaxAmount" caption="Tax" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="CumulativeBasis" caption="Cum. Disc. Basis" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="RecordNumber" caption="Record #" allowEditing={false} />
                                            <Column dataField="TaxableTotal" caption="Taxable" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="Stage" caption="Stage">
                                                <Lookup dataSource={OmTransactionStages} displayExpr="value" valueExpr="key" />
                                            </Column>
                                            <Column dataField="DiscountPercent" caption="Discount" dataType="number" format="#,##0.00" />
                                            <Column dataField="ShippingDate" caption="Shipping Date" dataType="date" format="shortDate" />
                                        </DataGrid>
                                    </div>
                                </div>
                            </TabItem>
                            <TabItem title="Discount SubDetail">
                                <div className="cumulative-discount-tab-body">
                                    <div className="cumulative-discount-effective-date">
                                        <DateBox
                                            label="Effective Date"
                                            labelMode="floating"
                                            value={effectiveDate}
                                            readOnly
                                            displayFormat="MM/dd/yyyy"
                                            stylingMode="outlined"
                                            width={200}
                                        />
                                    </div>
                                    <div className="cumulative-discount-grid">
                                        <DataGrid
                                            dataSource={subDetails}
                                            keyExpr="_key"
                                            height="100%"
                                            showBorders={true}
                                            columnAutoWidth={true}
                                        >
                                            <Scrolling mode="standard" />
                                            <Paging defaultPageSize={10} />
                                            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 25, 50]} />
                                            <Column dataField="Description" caption="Description" />
                                            <Column dataField="RangeTo" caption="Range To" dataType="number" format={CURRENCY_FORMAT} />
                                            <Column dataField="DiscountPercent" caption="Discount %" dataType="number" format="#0.##'%'" />
                                        </DataGrid>
                                    </div>
                                </div>
                            </TabItem>
                        </TabPanel>
                    </div>
                </Popup>
            )}
        </>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CalculateCumulativeDiscountPopup);
